import type { RawRow } from '@/features/import/types';
import type { CircuitType, Issue, NormalizationStats, NormalizedField, NormalizedFieldsMap, QualityLevel } from '@/features/import/normalizer.types';
import type { ColumnKey, ColumnMapping, EdgeRecord, LocationAliasMap } from '@/features/network/models';

const SPECIAL_EMPTY_UNKNOWN = new Set(['', '-', '--', 'null', 'n/a', 'na', 'none', 'no', 'unknown', 'undefined']);
const SPECIAL_NOT_APPLICABLE = new Set(['not applicable', 'n/app', 'n.a.', 'not_applicable']);

const CIRCUIT_TYPE_MAP: Array<[RegExp, CircuitType]> = [
  [/^\s*l2\s*p2p\s*$/i, 'L2_P2P'],
  [/^\s*l1\s*lan\s*$/i, 'L1_LAN'],
  [/^\s*l1\s*wan\s*$/i, 'L1_WAN'],
  [/^\s*mpls\s*$/i, 'MPLS'],
];

const COLUMN_KEYS: ColumnKey[] = [
  'subject',
  'status',
  'serviceId',
  'internalId',
  'serviceType',
  'vendor',
  'supplierId',
  'oldCrmId',
  'from',
  'to',
  'typeOfCrossConnect',
  'portAPosition',
  'positionZ',
  'mrcTotal',
  'mrcTax',
  'mrc',
  'slaRtdMs',
  'nrc',
  'arc',
  'circuitType',
  'expectedRtdMs',
  'capacityMbps',
  'vendorName',
];

function mkIssue(field: string, code: Issue['code'], severity: QualityLevel, message: string, raw?: unknown): Issue {
  return { field, code, severity, message, raw };
}

function maxSeverity(a: QualityLevel, b: QualityLevel): QualityLevel {
  const rank: Record<QualityLevel, number> = { ok: 0, fixed: 1, warning: 2, error: 3 };
  return rank[a] >= rank[b] ? a : b;
}

function dedupeSpaces(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

function maybeFixBrokenEncoding(input: string): { text: string; fixed: boolean } {
  let text = input;
  const before = text;

  text = text
    .replace(/Â±/g, '±')
    .replace(/Ð¼Ñ/g, 'мс')
    .replace(/Â/g, '')
    .replace(/\u0000/g, '');

  return { text, fixed: text !== before };
}

function cleanText(raw: unknown, field: string): NormalizedField<string> {
  const issues: Issue[] = [];
  if (raw == null) {
    return {
      raw,
      cleanedText: null,
      state: 'empty',
      value: null,
      quality: 'ok',
      issues,
      manualReview: false,
    };
  }

  let text = String(raw).trim();
  const encoding = maybeFixBrokenEncoding(text);
  text = dedupeSpaces(encoding.text);

  let quality: QualityLevel = 'ok';

  if (encoding.fixed) {
    issues.push(mkIssue(field, 'BROKEN_ENCODING_FIXED', 'fixed', 'Исправлены признаки битой кодировки', raw));
    quality = maxSeverity(quality, 'fixed');
  }

  const lower = text.toLowerCase();
  if (SPECIAL_NOT_APPLICABLE.has(lower)) {
    return {
      raw,
      cleanedText: text,
      state: 'not_applicable',
      value: null,
      quality,
      issues,
      manualReview: false,
    };
  }

  if (SPECIAL_EMPTY_UNKNOWN.has(lower)) {
    issues.push(mkIssue(field, 'SPECIAL_EMPTY_TOKEN', 'fixed', 'Специальное значение приведено к модели отсутствия', raw));
    return {
      raw,
      cleanedText: text,
      state: 'unknown',
      value: null,
      quality: maxSeverity(quality, 'fixed'),
      issues,
      manualReview: false,
    };
  }

  return {
    raw,
    cleanedText: text,
    state: text === '' ? 'empty' : 'value',
    value: text === '' ? null : text,
    quality,
    issues,
    manualReview: false,
  };
}

function looksLikeExcelAutoDate(text: string): boolean {
  const value = text.toLowerCase();
  return /^\d{1,2}-[а-яё]+\.?$/i.test(value) || /^[а-яё]+\.-\d{2}$/i.test(value) || /^\d{1,2}-[а-яё]+$/i.test(value);
}

function parsePlainNumber(text: string): number | null {
  const normalized = text.replace(/\s+/g, '').replace(',', '.').replace(/[^0-9.+-]/g, '');
  if (!normalized || normalized === '.' || normalized === '-' || normalized === '+') return null;
  const value = Number(normalized);
  return Number.isFinite(value) ? value : null;
}

function normalizeLocationText(raw: unknown, field: string): NormalizedField<string> {
  const result = cleanText(raw, field);
  if (!result.value) return result;
  const normalized = result.value.replace(/\s*,\s*/g, ', ').replace(/\s*-\s*/g, ' - ');
  return { ...result, value: normalized, cleanedText: normalized };
}

function parseStringField(raw: unknown, field: string): NormalizedField<string> {
  return cleanText(raw, field);
}

function parseMoneyField(raw: unknown, field: string): NormalizedField<number> {
  const base = cleanText(raw, field);
  const issues = [...base.issues];
  let quality = base.quality;
  let manualReview = base.manualReview;

  if (base.state !== 'value' || !base.value) {
    return { ...base, value: null, issues, quality, manualReview };
  }

  if (looksLikeExcelAutoDate(base.value)) {
    issues.push(mkIssue(field, 'EXCEL_AUTO_DATE_DETECTED', 'error', 'Похоже на Excel-автодату', raw));
    return { raw, cleanedText: base.value, state: 'invalid', value: null, quality: 'error', issues, manualReview: true };
  }

  const value = parsePlainNumber(base.value);
  if (value == null) {
    issues.push(mkIssue(field, 'TEXT_IN_NUMERIC_FIELD', 'error', 'Не удалось распарсить денежное поле', raw));
    return { raw, cleanedText: base.value, state: 'invalid', value: null, quality: 'error', issues, manualReview: true };
  }

  issues.push(mkIssue(field, 'MONEY_CURRENCY_UNKNOWN', 'warning', 'Валюта не указана', raw));
  quality = maxSeverity(quality, 'warning');

  if (value === 0) {
    issues.push(mkIssue(field, 'ZERO_IS_AMBIGUOUS', 'warning', 'Ноль неоднозначен: это может быть zero, unknown или not_applicable', raw));
    quality = maxSeverity(quality, 'warning');
    manualReview = true;
    return { raw, cleanedText: base.value, state: 'zero', value, quality, issues, manualReview };
  }

  return { raw, cleanedText: base.value, state: 'value', value, quality, issues, manualReview };
}

function parseLatencyField(raw: unknown, field: string): NormalizedField<number> {
  const base = cleanText(raw, field);
  const issues = [...base.issues];
  let quality = base.quality;
  let manualReview = base.manualReview;

  if (base.state !== 'value' || !base.value) {
    return { ...base, value: null, issues, quality, manualReview };
  }

  const text = base.value;
  const lower = text.toLowerCase();

  if (looksLikeExcelAutoDate(lower)) {
    issues.push(mkIssue(field, 'EXCEL_AUTO_DATE_DETECTED', 'error', 'Похоже на Excel-автодату вместо RTD', raw));
    return { raw, cleanedText: text, state: 'invalid', value: null, quality: 'error', issues, manualReview: true };
  }

  if (/availability|pud|check description|sla tested|description|route|маршрут/i.test(text)) {
    issues.push(mkIssue(field, 'UNSUPPORTED_MEANING', 'error', 'В поле RTD лежит не задержка, а другой KPI или текст', raw));
    return { raw, cleanedText: text, state: 'invalid', value: null, quality: 'error', issues, manualReview: true };
  }

  if (/os\d+/i.test(text) || /=/.test(text)) {
    issues.push(mkIssue(field, 'FORMULA_REFERENCE', 'warning', 'Ссылка на другие сервисы или формула требует ручной проверки', raw));
    quality = maxSeverity(quality, 'warning');
    manualReview = true;
  }

  if (/±/.test(text)) {
    issues.push(mkIssue(field, 'APPROX_VALUE', 'warning', 'Приблизительное значение RTD', raw));
    quality = maxSeverity(quality, 'warning');
    manualReview = true;
  }

  if (/[~-]/.test(text) && /\d/.test(text)) {
    issues.push(mkIssue(field, 'AMBIGUOUS_RANGE', 'warning', 'Диапазон RTD сведён к одному числу', raw));
    quality = maxSeverity(quality, 'warning');
    manualReview = true;
  }

  const values = lower.match(/-?\d+(?:[.,]\d+)?/g)?.map((item) => Number(item.replace(',', '.'))) ?? [];
  if (values.length === 0) {
    issues.push(mkIssue(field, 'TEXT_IN_NUMERIC_FIELD', 'error', 'Не удалось выделить число RTD', raw));
    return { raw, cleanedText: text, state: 'invalid', value: null, quality: 'error', issues, manualReview: true };
  }

  let value = values[0];
  if (values.length >= 2 && /[~-]/.test(text) && !/±/.test(text)) {
    value = (values[0] + values[1]) / 2;
  }

  if (/us|μs/.test(lower)) {
    value = value / 1000;
    issues.push(mkIssue(field, 'NON_CANONICAL_UNIT', 'fixed', 'Микросекунды приведены к миллисекундам', raw));
    quality = maxSeverity(quality, 'fixed');
  } else if (/min/.test(lower)) {
    value = value * 60 * 1000;
    issues.push(mkIssue(field, 'NON_CANONICAL_UNIT', 'fixed', 'Минуты приведены к миллисекундам', raw));
    quality = maxSeverity(quality, 'fixed');
    manualReview = true;
  } else if (/ms|мс/.test(lower)) {
    // canonical already
  } else {
    issues.push(mkIssue(field, 'AMBIGUOUS_UNIT', 'warning', 'Единица RTD не указана, предполагаем ms', raw));
    quality = maxSeverity(quality, 'warning');
    manualReview = true;
  }

  return { raw, cleanedText: text, state: value === 0 ? 'zero' : 'value', value, quality, issues, manualReview };
}

function parseCapacityField(raw: unknown, field: string): NormalizedField<number> {
  const base = cleanText(raw, field);
  const issues = [...base.issues];
  let quality = base.quality;
  let manualReview = base.manualReview;

  if (base.state !== 'value' || !base.value) {
    return { ...base, value: null, issues, quality, manualReview };
  }

  const text = base.value;
  const lower = text.toLowerCase();

  if (looksLikeExcelAutoDate(lower)) {
    issues.push(mkIssue(field, 'EXCEL_AUTO_DATE_DETECTED', 'error', 'Похоже на Excel-автодату вместо Capacity', raw));
    return { raw, cleanedText: text, state: 'invalid', value: null, quality: 'error', issues, manualReview: true };
  }

  if (/\?/.test(text)) {
    issues.push(mkIssue(field, 'AMBIGUOUS_UNIT', 'warning', 'Подозрительное значение Capacity', raw));
    quality = maxSeverity(quality, 'warning');
    manualReview = true;
  }

  const value = parsePlainNumber(text);
  if (value == null) {
    issues.push(mkIssue(field, 'TEXT_IN_NUMERIC_FIELD', 'error', 'Не удалось распарсить Capacity', raw));
    return { raw, cleanedText: text, state: 'invalid', value: null, quality: 'error', issues, manualReview: true };
  }

  let mbps: number;
  if (/tbit\/s|tbps|tb\b|t\b/i.test(text)) {
    mbps = value * 1_000_000;
    issues.push(mkIssue(field, 'NON_CANONICAL_UNIT', 'fixed', 'Tbps/Tb приведены к Mbps', raw));
    quality = maxSeverity(quality, 'fixed');
  } else if (/gbit\/s|gbps|gb\b|g\b/i.test(text)) {
    mbps = value * 1000;
    issues.push(mkIssue(field, 'NON_CANONICAL_UNIT', 'fixed', 'Gbps/Gb приведены к Mbps', raw));
    quality = maxSeverity(quality, 'fixed');
  } else if (/mbit\/s|mbps|mb\b|m\b/i.test(text)) {
    mbps = value;
    if (!/mbps/i.test(text)) {
      issues.push(mkIssue(field, 'NON_CANONICAL_UNIT', 'fixed', 'Capacity приведён к каноническому Mbps', raw));
      quality = maxSeverity(quality, 'fixed');
    }
  } else if (/kbit\/s|kbps|kb\b|k\b/i.test(text)) {
    mbps = value / 1000;
    issues.push(mkIssue(field, 'NON_CANONICAL_UNIT', 'fixed', 'Kbps/Kb приведены к Mbps', raw));
    quality = maxSeverity(quality, 'fixed');
  } else {
    mbps = value === 1 || value === 10 || value === 100 ? value * 1000 : value;
    issues.push(mkIssue(field, 'AMBIGUOUS_UNIT', 'warning', 'Единица Capacity не указана, применена эвристика', raw));
    quality = maxSeverity(quality, 'warning');
    manualReview = true;
  }

  return { raw, cleanedText: text, state: mbps === 0 ? 'zero' : 'value', value: mbps, quality, issues, manualReview };
}

function parseCircuitTypeField(raw: unknown, field: string): NormalizedField<CircuitType> {
  const base = cleanText(raw, field);
  const issues = [...base.issues];
  let quality = base.quality;

  if (base.state === 'empty' || base.state === 'unknown' || base.state === 'not_applicable') {
    return {
      raw,
      cleanedText: base.cleanedText,
      state: base.state,
      value: 'UNKNOWN',
      quality: base.state === 'unknown' ? maxSeverity(quality, 'warning') : quality,
      issues,
      manualReview: base.state !== 'empty',
    };
  }

  const text = base.value ?? '';
  for (const [pattern, circuitType] of CIRCUIT_TYPE_MAP) {
    if (pattern.test(text)) {
      return { raw, cleanedText: text, state: 'value', value: circuitType, quality, issues, manualReview: false };
    }
  }

  issues.push(mkIssue(field, 'INVALID_CIRCUIT_TYPE', 'warning', 'Значение не входит в контролируемый справочник', raw));
  quality = maxSeverity(quality, 'warning');
  return { raw, cleanedText: text, state: 'invalid', value: 'UNKNOWN', quality, issues, manualReview: true };
}

function validateMoneyTrio(mrcTotal: NormalizedField<number>, mrcTax: NormalizedField<number>, mrc: NormalizedField<number>): Issue[] {
  if (mrcTotal.value == null || mrcTax.value == null || mrc.value == null) return [];
  const actual = mrc.value + mrcTax.value;
  if (Math.abs(actual - mrcTotal.value) <= 0.01) return [];

  return [mkIssue('MRC Total', 'MONEY_TRIO_MISMATCH', 'warning', `Связка денег не сходится: MRC + MRC Tax = ${actual}, а MRC Total = ${mrcTotal.value}`, {
    mrc: mrc.value,
    mrcTax: mrcTax.value,
    mrcTotal: mrcTotal.value,
  })];
}

function applyLocationAlias(value: string | null, aliases: LocationAliasMap) {
  if (!value) return null;
  return aliases[value] ? normalizeLocationText(aliases[value], 'Alias').value : value;
}

function buildMappedRow(row: RawRow, mapping: ColumnMapping) {
  const mapped: Partial<Record<ColumnKey, unknown>> = {};
  for (const key of COLUMN_KEYS) {
    const header = mapping[key];
    mapped[key] = header ? row[header] : '';
  }
  return mapped;
}

function buildNotes(issues: Issue[], aliasNotes: string[]) {
  const messages = issues
    .filter((issue) => issue.severity !== 'ok')
    .map((issue) => `${issue.field}: ${issue.message}`);
  return Array.from(new Set([...aliasNotes, ...messages]));
}

function requireGraphField(fieldName: string, field: NormalizedField<unknown>, issues: Issue[]) {
  if (field.state === 'empty' || field.state === 'unknown' || field.state === 'invalid') {
    issues.push(mkIssue(fieldName, 'FIELD_REQUIRED_FOR_GRAPH', 'error', 'Поле обязательно для построения графа', field.raw));
  }
}

function aggregateQuality(fields: NormalizedFieldsMap, extraIssues: Issue[]) {
  let quality: QualityLevel = 'ok';
  let manualReview = false;
  const issues: Issue[] = [...extraIssues];

  for (const field of Object.values(fields) as Array<NormalizedField<unknown> | undefined>) {
    if (!field) continue;
    quality = maxSeverity(quality, field.quality);
    manualReview = manualReview || field.manualReview;
    issues.push(...field.issues);
  }

  for (const issue of extraIssues) {
    quality = maxSeverity(quality, issue.severity);
    manualReview = manualReview || issue.severity === 'warning' || issue.severity === 'error';
  }

  return { quality, manualReview, issues };
}

export type NormalizedRowAudit = {
  index: number;
  raw: RawRow;
  mapped: Partial<Record<ColumnKey, unknown>>;
  fields: NormalizedFieldsMap;
  edge: EdgeRecord | null;
  quality: QualityLevel;
  issues: Issue[];
  manualReview: boolean;
};

export type NormalizationResult = {
  rows: NormalizedRowAudit[];
  graphEdges: EdgeRecord[];
  rejectedRows: NormalizedRowAudit[];
  stats: NormalizationStats;
};

export function normalizeRowsDetailed(rows: RawRow[], mapping: ColumnMapping, aliases: LocationAliasMap = {}): NormalizationResult {
  const audits = rows.map((row, index) => normalizeMappedRow(row, index, mapping, aliases));
  const graphEdges = audits.map((audit) => audit.edge).filter((edge): edge is EdgeRecord => edge != null);
  const rejectedRows = audits.filter((audit) => audit.edge == null);

  const stats: NormalizationStats = {
    total: audits.length,
    acceptedToGraph: graphEdges.length,
    rejected: rejectedRows.length,
    warnings: audits.filter((audit) => audit.quality === 'warning').length,
    errors: audits.filter((audit) => audit.quality === 'error').length,
    manualReview: audits.filter((audit) => audit.manualReview).length,
    autoFixed: audits.filter((audit) => audit.quality === 'fixed').length,
  };

  return { rows: audits, graphEdges, rejectedRows, stats };
}

export function normalizeRows(rows: RawRow[], mapping: ColumnMapping, aliases: LocationAliasMap = {}) {
  return normalizeRowsDetailed(rows, mapping, aliases).graphEdges;
}

export function normalizeMappedRow(row: RawRow, index: number, mapping: ColumnMapping, aliases: LocationAliasMap = {}): NormalizedRowAudit {
  const mapped = buildMappedRow(row, mapping);

  const subject = parseStringField(mapped.subject, 'Subject');
  const status = parseStringField(mapped.status, 'Status');
  const serviceId = parseStringField(mapped.serviceId, 'Service ID');
  const internalId = parseStringField(mapped.internalId, 'Internal ID');
  const serviceType = parseStringField(mapped.serviceType, 'Service Type');
  const vendor = parseStringField(mapped.vendorName || mapped.vendor, 'Vendor');
  const supplierId = parseStringField(mapped.supplierId, 'Supplier ID');
  const oldCrmId = parseStringField(mapped.oldCrmId, 'Old CRM ID');
  const typeOfCrossConnect = parseStringField(mapped.typeOfCrossConnect, 'Type of Cross Connect');
  const portAPosition = parseStringField(mapped.portAPosition, 'Port A Position');
  const positionZ = parseStringField(mapped.positionZ, 'Position Z');
  const locationA = normalizeLocationText(mapped.from, 'Location A');
  const locationZ = normalizeLocationText(mapped.to, 'Location Z');
  const slaRtdMs = parseLatencyField(mapped.slaRtdMs, 'SLA RTD');
  const expectedRtdMs = parseLatencyField(mapped.expectedRtdMs, 'Expected RTD');
  const capacityMbps = parseCapacityField(mapped.capacityMbps, 'Capacity');
  const circuitType = parseCircuitTypeField(mapped.circuitType, 'Circuit type');
  const mrcTotal = parseMoneyField(mapped.mrcTotal, 'MRC Total');
  const mrcTax = parseMoneyField(mapped.mrcTax, 'MRC Tax');
  const mrc = parseMoneyField(mapped.mrc, 'MRC');
  const nrc = parseMoneyField(mapped.nrc, 'NRC');
  const arc = parseMoneyField(mapped.arc, 'ARC');

  const aliasNotes: string[] = [];
  const aliasedA = applyLocationAlias(locationA.value, aliases);
  const aliasedZ = applyLocationAlias(locationZ.value, aliases);
  if (locationA.value && aliasedA && locationA.value !== aliasedA) aliasNotes.push(`Location A объединена в «${aliasedA}»`);
  if (locationZ.value && aliasedZ && locationZ.value !== aliasedZ) aliasNotes.push(`Location Z объединена в «${aliasedZ}»`);

  const fields: NormalizedFieldsMap = {
    subject,
    status,
    serviceId,
    internalId,
    serviceType,
    vendor,
    supplierId,
    oldCrmId,
    from: { ...locationA },
    to: { ...locationZ },
    typeOfCrossConnect,
    portAPosition,
    positionZ,
    mrcTotal,
    mrcTax,
    mrc,
    slaRtdMs,
    nrc,
    arc,
    circuitType,
    expectedRtdMs,
    capacityMbps,
    vendorName: vendor,
  };

  const extraIssues = validateMoneyTrio(mrcTotal, mrcTax, mrc);
  requireGraphField('Location A', locationA, extraIssues);
  requireGraphField('Location Z', locationZ, extraIssues);

  const latencyValue = expectedRtdMs.value ?? slaRtdMs.value ?? null;
  const latencySource: EdgeRecord['latencySource'] = expectedRtdMs.value != null ? 'expected_rtd' : slaRtdMs.value != null ? 'sla_rtd' : 'none';

  if (latencyValue == null) {
    extraIssues.push(mkIssue('RTD', 'INCOMPLETE_ROUTE_EDGE', 'warning', 'Для ребра нет пригодной задержки; маршрут можно строить только в cost/hops режимах'));
  }

  if (capacityMbps.value == null) {
    extraIssues.push(mkIssue('Capacity', 'INCOMPLETE_ROUTE_EDGE', 'warning', 'Для ребра не указана пропускная способность; фильтрация по capacity будет неточной'));
  }

  const aggregate = aggregateQuality(fields, extraIssues);
  const from = aliasedA ?? locationA.value;
  const to = aliasedZ ?? locationZ.value;
  const blocking = extraIssues.some((issue) => issue.code === 'FIELD_REQUIRED_FOR_GRAPH' && issue.severity === 'error') || !from || !to;

  const edge: EdgeRecord | null = blocking
    ? null
    : {
        id: serviceId.value || internalId.value || subject.value || `edge-${index}`,
        subject: subject.value || '',
        status: status.value || 'Unknown',
        serviceId: serviceId.value || '',
        internalId: internalId.value || '',
        serviceType: serviceType.value || '',
        vendor: vendor.value || '',
        supplierId: supplierId.value || '',
        oldCrmId: oldCrmId.value || '',
        from,
        to,
        typeOfCrossConnect: typeOfCrossConnect.value || '',
        portAPosition: portAPosition.value || '',
        positionZ: positionZ.value || '',
        mrcTotal: mrcTotal.value,
        mrcTax: mrcTax.value,
        mrc: mrc.value,
        slaRtdMs: slaRtdMs.value,
        nrc: nrc.value,
        arc: arc.value,
        circuitType: circuitType.value || 'UNKNOWN',
        expectedRtdMs: expectedRtdMs.value,
        capacityMbps: capacityMbps.value,
        vendorName: vendor.value || '',
        notes: buildNotes(aggregate.issues, aliasNotes),
        quality: aggregate.quality,
        manualReview: aggregate.manualReview,
        issues: aggregate.issues,
        latencySource,
      };

  return {
    index,
    raw: row,
    mapped,
    fields,
    edge,
    quality: edge?.quality ?? aggregate.quality,
    issues: aggregate.issues,
    manualReview: edge?.manualReview ?? aggregate.manualReview,
  };
}
